import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, TrendingUp, Calendar, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import BottomNav from '../components/BottomNav';

const COLORS = {
  beige: '#F5F3ED',
  navy: '#1a1a3e',
  orange: '#FF8C42',
  white: '#ffffff',
  gray: {
    100: '#f5f7f8',
    200: '#e8ecee',
    400: '#9ba4ab',
    500: '#6b7280',
    600: '#4c5b62'
  },
  success: '#10b981'
};

// Mock data for goals
const mockGoals = [
  {
    id: '1',
    name: 'Beach House',
    description: 'Beachfront property with pool',
    targetAmount: 2250000,
    currentAmount: 450000,
    targetDate: '2035-03-01',
    portfolio: 'Horizon',
    portfolioReturn: '+11.3%',
    progress: 20,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/767a8grf_image.webp'
  },
  {
    id: '2',
    name: 'Financial Freedom',
    description: 'Early retirement fund',
    targetAmount: 3500000,
    currentAmount: 750000,
    targetDate: '2040-01-01',
    portfolio: 'Velocity',
    portfolioReturn: '+13.8%',
    progress: 21,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/ex57r7mr_nick-fewings-GpplHQ-fyvA-unsplash.jpg'
  },
  {
    id: '3',
    name: "Children's Education",
    description: 'Future education fund',
    targetAmount: 850000,
    currentAmount: 425000,
    targetDate: '2028-06-01',
    portfolio: 'Fortress',
    portfolioReturn: '+9.1%',
    progress: 50,
    image: 'https://customer-assets.emergentagent.com/job_asset-harmony-1/artifacts/bjmak8cv_note-thanun-CYlPykF-qAM-unsplash.jpg'
  }
];

const Goals = () => {
  const navigate = useNavigate();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div style={{ 
      backgroundColor: COLORS.beige, 
      minHeight: '100vh',
      paddingTop: '80px',
      paddingBottom: '100px'
    }}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-5xl font-light" style={{ 
              fontFamily: 'Georgia, serif', 
              color: COLORS.navy 
            }}>
              Your Goals
            </h1>
            <Button style={{
              background: COLORS.orange,
              color: COLORS.white,
              borderRadius: '9999px',
              padding: '0.75rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Plus size={20} />
              New Goal
            </Button>
          </div>
          <p className="text-gray-600 text-lg">
            Track and manage your financial objectives
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '3rem'
        }}>
          <Card style={{ padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.5rem' }}>
              Total Goal Value
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: COLORS.navy,
              fontFamily: 'Georgia, serif'
            }}>
              {formatCurrency(mockGoals.reduce((sum, goal) => sum + goal.targetAmount, 0))}
            </div>
          </Card>

          <Card style={{ padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.5rem' }}>
              Current Progress
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: COLORS.success,
              fontFamily: 'Georgia, serif'
            }}>
              {formatCurrency(mockGoals.reduce((sum, goal) => sum + goal.currentAmount, 0))}
            </div>
          </Card>

          <Card style={{ padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.5rem' }}>
              Active Goals
            </div>
            <div style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: COLORS.navy,
              fontFamily: 'Georgia, serif'
            }}>
              {mockGoals.length}
            </div>
          </Card>
        </div>

        {/* Goal Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
          gap: '2rem'
        }}>
          {mockGoals.map((goal) => (
            <Card 
              key={goal.id}
              style={{
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: 'none'
              }}
              onClick={() => navigate(`/goal/${goal.id}`)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
            >
              {/* Goal Image */}
              <div style={{ position: 'relative', height: '200px', overflow: 'hidden' }}>
                <img 
                  src={goal.image} 
                  alt={goal.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: COLORS.orange,
                  color: COLORS.white,
                  padding: '0.5rem 1rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}>
                  {goal.portfolio}
                </div>
              </div>

              {/* Goal Info */}
              <div style={{ padding: '1.5rem' }}>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '300',
                  color: COLORS.navy,
                  marginBottom: '0.5rem',
                  fontFamily: 'Georgia, serif'
                }}>
                  {goal.name}
                </h3>
                <p style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '1rem' }}>
                  {goal.description}
                </p>

                {/* Progress Bar */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: COLORS.gray[500] }}>Progress</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: COLORS.navy }}>
                      {goal.progress}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: COLORS.gray[200],
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${goal.progress}%`,
                      height: '100%',
                      background: COLORS.orange,
                      borderRadius: '4px',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>

                {/* Stats Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: COLORS.gray[500], marginBottom: '0.25rem' }}>
                      Current
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: COLORS.navy }}>
                      {formatCurrency(goal.currentAmount)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: COLORS.gray[500], marginBottom: '0.25rem' }}>
                      Target
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: COLORS.navy }}>
                      {formatCurrency(goal.targetAmount)}
                    </div>
                  </div>
                </div>

                {/* Footer Stats */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '1rem',
                  borderTop: `1px solid ${COLORS.gray[200]}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <TrendingUp size={16} color={COLORS.success} />
                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: COLORS.success }}>
                      {goal.portfolioReturn}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Calendar size={16} color={COLORS.gray[400]} />
                    <span style={{ fontSize: '0.875rem', color: COLORS.gray[600] }}>
                      {formatDate(goal.targetDate)}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Goals;